import { translate } from 'locales'
import React from 'react'
import { useTheme } from 'styled-components'
import { ITheme } from 'theme/interface'
import { monthlyHistoryStatusLabelKey } from '../MonthlyHistory/statusLabels'
import { statusColor } from '../Overview/statusColors'
import {
  IFinancialConsultationRates,
  IFinancialConsultationsStats,
  MonthlyProductionStatus,
} from '../types'
import { formatCurrency, sumConsultations } from '../utils'
import * as S from './styles'

const PSYCHOLOGY_TOTAL_PASS_RATES = {
  consultation30: 20,
  consultation60: 40,
  noShow30: 6,
  noShow60: 12,
  canceled30: 6,
  canceled60: 12,
}

const NUTRITIONIST_TOTAL_PASS_RATES = {
  consultation: 25,
  noShow: 7.5,
  canceled: 7.5,
}

const DEFAULT_TOTAL_PASS_RATES = {
  consultation: 20,
  noShow: 6,
  canceled: 6,
}

interface SummaryCardsProps {
  totalValue: number
  stats: IFinancialConsultationsStats
  rates: IFinancialConsultationRates
  status: MonthlyProductionStatus
  locale: string
  isPsychology: boolean
  isNutritionist: boolean
  isGeneralPractitioner: boolean
  showTotalPassRates: boolean
}

const SummaryCards = ({
  totalValue,
  stats,
  rates,
  status,
  locale,
  isPsychology,
  isNutritionist,
  isGeneralPractitioner,
  showTotalPassRates,
}: SummaryCardsProps) => {
  const totalPassRates = isNutritionist
    ? NUTRITIONIST_TOTAL_PASS_RATES
    : DEFAULT_TOTAL_PASS_RATES
  const theme = useTheme() as ITheme
  const consultationsCount = sumConsultations(stats)
  const averageValue =
    consultationsCount > 0 ? totalValue / consultationsCount : 0

  const renderTotalPassRate = (value: number) =>
    showTotalPassRates ? (
      <>
        {' '}
        — {translate('financial.overview.totalpass')}{' '}
        {formatCurrency(value, locale)}
      </>
    ) : null

  return (
    <S.Grid>
      <S.Card>
        <S.CardTitle>{translate('financial.totalValue')}</S.CardTitle>

        <S.CardValue>{formatCurrency(totalValue, locale)}</S.CardValue>
      </S.Card>

      <S.Card>
        <S.CardTitle>
          {translate('financial.overview.attendedConsultations')}
        </S.CardTitle>

        <S.ConsultationsCount>{consultationsCount}</S.ConsultationsCount>

        <S.ConsultationsList>
          {isPsychology ? (
            <>
              <li>
                <S.Dot color={theme.colors.success} />
                {stats.finished30} {translate('financial.overview.finished30')}
              </li>
              <li>
                <S.Dot color={theme.colors.success} />
                {stats.finished60} {translate('financial.overview.finished60')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {stats.noShow30}{' '}
                {translate('financial.monthlyHistory.detail.noShowLabel30')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {stats.noShow60}{' '}
                {translate('financial.monthlyHistory.detail.noShowLabel60')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {stats.canceledUnder30_30}{' '}
                {translate('financial.overview.canceled30')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {stats.canceledUnder30_60}{' '}
                {translate('financial.overview.canceled60')}
              </li>
            </>
          ) : (
            <>
              <li>
                <S.Dot color={theme.colors.success} />
                {stats.finished30 + stats.finished60}{' '}
                {translate('financial.overview.finished')}
              </li>
              <li>
                <S.Dot color={theme.colors.warning} />
                {stats.noShow30 + stats.noShow60}{' '}
                {translate('financial.monthlyHistory.detail.noShowLabel')}
              </li>
              <li>
                <S.Dot color={theme.colors.error} />
                {stats.canceledUnder30_30 + stats.canceledUnder30_60}{' '}
                {translate('financial.overview.canceled')}
              </li>
            </>
          )}
        </S.ConsultationsList>
      </S.Card>

      <S.Card>
        <S.CardTitle>
          {translate('financial.monthlyHistory.detail.averageValue')}
        </S.CardTitle>

        <S.CardValue>{formatCurrency(averageValue, locale)}</S.CardValue>

        {isGeneralPractitioner ? null : (
          <S.RatesList>
            <span className="rates-label">
              {translate(
                'financial.monthlyHistory.detail.valuePerConsultation'
              )}
            </span>

            {isPsychology ? (
              <>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.finished30')}:{' '}
                  {formatCurrency(rates.consultation30, locale)}
                  {renderTotalPassRate(
                    PSYCHOLOGY_TOTAL_PASS_RATES.consultation30
                  )}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.finished60')}:{' '}
                  {formatCurrency(rates.consultation60, locale)}
                  {renderTotalPassRate(
                    PSYCHOLOGY_TOTAL_PASS_RATES.consultation60
                  )}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.noShow30')}:{' '}
                  {formatCurrency(rates.noShow30, locale)}
                  {renderTotalPassRate(PSYCHOLOGY_TOTAL_PASS_RATES.noShow30)}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.noShow60')}:{' '}
                  {formatCurrency(rates.noShow60, locale)}
                  {renderTotalPassRate(PSYCHOLOGY_TOTAL_PASS_RATES.noShow60)}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.canceled30')}:{' '}
                  {formatCurrency(rates.canceled30, locale)}
                  {renderTotalPassRate(PSYCHOLOGY_TOTAL_PASS_RATES.canceled30)}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.canceled60')}:{' '}
                  {formatCurrency(rates.canceled60, locale)}
                  {renderTotalPassRate(PSYCHOLOGY_TOTAL_PASS_RATES.canceled60)}
                </li>
              </>
            ) : (
              <>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.finished')}:{' '}
                  {formatCurrency(rates.consultation30, locale)}
                  {renderTotalPassRate(totalPassRates.consultation)}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.noShow')}:{' '}
                  {formatCurrency(rates.noShow30, locale)}
                  {renderTotalPassRate(totalPassRates.noShow)}
                </li>
                <li>
                  <S.Dot color="#4d4d4d" />
                  {translate('financial.overview.canceled')}:{' '}
                  {formatCurrency(rates.canceled30, locale)}
                  {renderTotalPassRate(totalPassRates.canceled)}
                </li>
              </>
            )}
          </S.RatesList>
        )}
      </S.Card>

      <S.Card>
        <S.CardTitle>
          {translate('financial.monthlyHistory.detail.status')}
        </S.CardTitle>

        <S.StatusValue color={statusColor(status)}>
          <S.Dot color={statusColor(status)} size={10} />
          {translate(monthlyHistoryStatusLabelKey(status))}
        </S.StatusValue>
      </S.Card>
    </S.Grid>
  )
}

export default SummaryCards
